import { type ApiResponse, post } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

const createReviewUnlockResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.literal("review-unlock"),
    attributes: z.object({
      areReviewsUnlocked: z.boolean(),
      temporaryUnlockedUntil: z.string().datetime().nullable(),
    }),
  }),
});

export type CreateReviewUnlockResponse = z.infer<typeof createReviewUnlockResponseSchema>;

function getCreateReviewUnlockUrl(): string {
  return `${environmentConfig.REACT_APP_SHIFT_REVIEW_API_URL}/workers/review-unlocks`;
}

export function useCreateReviewUnlock(
  options: UseMutationOptions<ApiResponse<CreateReviewUnlockResponse>, AxiosError, void> = {}
): UseMutationResult<ApiResponse<CreateReviewUnlockResponse>, AxiosError, void> {
  const queryClient = useQueryClient();
  const workplaceCommentsPrefix = `${environmentConfig.REACT_APP_SHIFT_REVIEW_API_URL}/workplaces/`;

  const { onSuccess: userOnSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: async () => {
      return await post({
        url: getCreateReviewUnlockUrl(),
        requestSchema: z.undefined(),
        responseSchema: createReviewUnlockResponseSchema,
      });
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        predicate: (query) => {
          return query.queryKey.some(
            (key) =>
              typeof key === "string" &&
              key.startsWith(workplaceCommentsPrefix) &&
              key.endsWith("/comments")
          );
        },
      });
      await userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.POST_REVIEW_UNLOCK_FAILURE,
      ...restOptions.meta,
    },
  });
}
